// Helper functions para migrar de moment a date-fns
import { format, parseISO, isValid, addDays, addMonths, addYears, startOfDay, endOfDay, isAfter, isBefore, isEqual } from 'date-fns';

// Formatos comunes
export const DATE_FORMATS = {
  DISPLAY: 'dd/MM/yyyy',
  DISPLAY_WITH_TIME: 'dd/MM/yyyy HH:mm:ss',
  ISO: 'yyyy-MM-dd',
  API: "yyyy-MM-dd'T'HH:mm:ss",
  SHORT: 'dd/MM/yy'
};

// Reemplazo de moment().format()
export const formatDate = (date, formatStr = DATE_FORMATS.DISPLAY) => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
    if (!isValid(dateObj)) return '';
    return format(dateObj, formatStr);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

// Reemplazo de moment() para parsear fechas con formato específico
// Normaliza valores de fecha para componentes DatePicker que esperan dd/MM/yyyy.
export const formatDatePickerDefaultValue = (dateValue) => {
  if (!dateValue) return '';

  if (typeof dateValue === 'string') {
    const ymdMatch = dateValue.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
    if (ymdMatch) {
      const [, year, month, day] = ymdMatch;
      return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
    }
  }

  return formatDate(dateValue, DATE_FORMATS.DISPLAY);
};

// Reemplazo de moment() para parsear fechas con formato especifico
export const parseDate = (dateString, formatStr = DATE_FORMATS.DISPLAY) => {
  if (!dateString) return null;
  
  try {
    // Si el formato es dd/MM/yyyy o dd/MM/yyyy HH:mm:ss, necesitamos parsear manualmente
    if (formatStr.includes('dd/MM/yyyy')) {
      const parts = dateString.split(/[\s\/:]/);
      if (parts.length >= 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Los meses en JS son 0-11
        const year = parseInt(parts[2], 10);
        const hour = parts.length > 3 ? parseInt(parts[3], 10) : 0;
        const minute = parts.length > 4 ? parseInt(parts[4], 10) : 0;
        const second = parts.length > 5 ? parseInt(parts[5], 10) : 0;
        
        return new Date(year, month, day, hour, minute, second);
      }
    }
    
    // Para otros formatos, intentar con parseISO primero
    return parseISO(dateString);
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};

// Reemplazo de moment().isValid()
export const isValidDate = (date) => {
  if (!date) return false;
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return isValid(dateObj);
};

// Reemplazo de moment().add()
export const addTime = (date, amount, unit) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  
  switch (unit) {
    case 'days':
      return addDays(dateObj, amount);
    case 'months':
      return addMonths(dateObj, amount);
    case 'years':
      return addYears(dateObj, amount);
    default:
      return dateObj;
  }
};

// Reemplazo de moment().startOf('day')
export const startOfDayFn = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return startOfDay(dateObj);
};

// Reemplazo de moment().endOf('day')
export const endOfDayFn = (date) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return endOfDay(dateObj);
};

// Reemplazo de moment().isAfter()
export const isAfterFn = (date, compareDate) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  const compareObj = typeof compareDate === 'string' ? parseISO(compareDate) : new Date(compareDate);
  return isAfter(dateObj, compareObj);
};

// Reemplazo de moment().isBefore()
export const isBeforeFn = (date, compareDate) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  const compareObj = typeof compareDate === 'string' ? parseISO(compareDate) : new Date(compareDate);
  return isBefore(dateObj, compareObj);
};

// Reemplazo de moment().isSame()
export const isSameFn = (date, compareDate) => {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  const compareObj = typeof compareDate === 'string' ? parseISO(compareDate) : new Date(compareDate);
  return isEqual(dateObj, compareObj);
};

// Función para obtener fecha actual en formato específico
export const getCurrentDate = (formatStr = DATE_FORMATS.DISPLAY) => {
  return format(new Date(), formatStr);
};

// Función para parsear fecha de API
export const parseDateFromAPI = (dateString) => {
  if (!dateString) return null;
  try {
    return parseISO(dateString);
  } catch (error) {
    console.error('Error parsing date from API:', error);
    return null;
  }
};

// Alias para compatibilidad con código existente
export const formatDateTime = formatDate;

// Exportar un objeto compatible con moment para migración gradual
export const momentCompat = {
  format: formatDate,
  isValid: isValidDate,
  add: addTime,
  startOf: (unit) => startOfDayFn(new Date()),
  endOf: (unit) => endOfDayFn(new Date()),
  isAfter: isAfterFn,
  isBefore: isBeforeFn,
  isSame: isSameFn,
  now: () => new Date()
};
